/**
 * SIGNALEMENTS — LE MODÈLE PUR DE L'INTERFACE (09/10/2026).
 *
 * ═══ TECHNIQUE EST LA SOURCE DE VÉRITÉ ═══
 * Un Signalement vit chez Technique (le service Technique) et nulle part
 * ailleurs : statut, priorité, historique, commentaires, liens, fichiers.
 * GESTION l'affiche et le relaie par son serveur (le BFF, contrat
 * `docs/technique/signalements-bff-v1.md`) ; le navigateur ne connaît ni
 * l'adresse de Technique, ni son secret, et ne garde rien au-delà de l'écran.
 *
 * Ce module ne contient NI React NI appel réseau : il se teste hors
 * navigateur. Il porte les règles qui se perdent le plus facilement :
 *  · lire une réponse du serveur sans la croire sur parole (validation à la
 *    main, comme `packages/core/src/services/contract.ts`) ;
 *  · filtres ↔ adresse, curseur opaque, reprise sur `INVALID_CURSOR` ;
 *  · ton du catalogue → classe LIVE CONTROL (un MAPPING VISUEL, rien d'autre) ;
 *  · actions = `transitions_possibles` (dites par Technique) ∩ droits GESTION
 *    ∩ exigences que l'écran sait remplir — jamais de machine à états locale ;
 *  · ce qui a changé entre deux lectures d'une fiche (conflit de version) ;
 *  · une clé d'idempotence par INTENTION, pas par clic ;
 *  · des erreurs dites selon leur cause (Technique absent ≠ droit GESTION).
 *
 * Les catalogues (libellés, tons, transitions, exigences, limites, types
 * d'événements) viennent TOUS de `GET /meta`. Rien ici ne les recopie.
 */

/* ═══════════════════════ lecture tolérante ═══════════════════════ */

type Objet = Record<string, unknown>

function objet(v: unknown): Objet | null {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Objet) : null
}
function texte(v: unknown): string | null { return typeof v === 'string' ? v : null }
function entier(v: unknown): number | null { return typeof v === 'number' && Number.isInteger(v) ? v : null }
function textes(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
}
/** Un identifiant arrive en chaîne ou en nombre (Technique sérialise `entity_id` en chaîne, l'id d'un objet en nombre). */
function ident(v: unknown): string | null {
  if (typeof v === 'string' && v !== '') return v
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return null
}

/* ═══════════════════════ le catalogue (`GET /meta`) ═══════════════════════ */

export interface CodeLibelle { code: string; label: string }
export interface StatutMeta extends CodeLibelle { actif: boolean; tone: string; transitions: string[]; requires: string[] }
export interface PrioriteMeta extends CodeLibelle { rang: number; tone: string }
export interface ImpactMeta extends CodeLibelle { tone: string; description: string | null }
export interface TypeFichier { mime: string; extension: string }

export interface MetaSignalements {
  statuts: StatutMeta[]
  reouverture_requires: string[]
  priorites: PrioriteMeta[]
  priorite_defaut: string
  impacts: ImpactMeta[]
  impact_defaut: string
  categories: CodeLibelle[]
  sources: CodeLibelle[]
  types_relation: CodeLibelle[]
  fichiers: { types: TypeFichier[]; taille_max: number; max_par_signalement: number }
  limites: Record<string, number>
  evenements: CodeLibelle[]
  priorites_elevees: string[]
}

/** Droits GESTION de la session, calculés par le serveur et rendus avec `/meta`. */
export interface Droits { read: boolean; create: boolean; update: boolean; manage: boolean }

export const AUCUN_DROIT: Droits = { read: false, create: false, update: false, manage: false }

function codesLibelles(v: unknown): CodeLibelle[] {
  if (!Array.isArray(v)) return []
  const out: CodeLibelle[] = []
  for (const x of v) {
    const o = objet(x)
    const code = texte(o?.['code'])
    const label = texte(o?.['label'])
    if (code && label) out.push({ code, label })
  }
  return out
}

/** Limites par défaut quand `/meta` n'en dit rien : celles du contrat Technique au 09/10/2026. */
const LIMITES_CONTRAT: Record<string, number> = {
  titre: 200, description: 10000, commentaire: 5000, emplacement: 120,
  resolution_summary: 2000, motif: 2000, page_defaut: 50, page_max: 200,
  activite_defaut: 100, activite_max: 500, recherche: 200,
}

/**
 * `{ meta, permissions }` → le catalogue et les droits, ou `null` si la forme
 * n'est pas celle du contrat. Les énumérations sont prises telles que Technique
 * les donne : une valeur nouvelle chez Technique apparaît ici sans déploiement.
 */
export function lireMeta(v: unknown): { meta: MetaSignalements; droits: Droits } | null {
  const racine = objet(v)
  const m = objet(racine?.['meta'])
  if (!m) return null
  const statuts: StatutMeta[] = []
  for (const x of Array.isArray(m['statuts']) ? m['statuts'] : []) {
    const o = objet(x)
    const code = texte(o?.['code'])
    const label = texte(o?.['label'])
    if (!o || !code || !label) continue
    statuts.push({
      code, label, actif: o['actif'] === true, tone: texte(o['tone']) ?? 'neutral',
      transitions: textes(o['transitions']), requires: textes(o['requires']),
    })
  }
  const priorites: PrioriteMeta[] = []
  for (const x of Array.isArray(m['priorites']) ? m['priorites'] : []) {
    const o = objet(x)
    const code = texte(o?.['code'])
    const label = texte(o?.['label'])
    if (!o || !code || !label) continue
    priorites.push({ code, label, rang: entier(o['rang']) ?? 0, tone: texte(o['tone']) ?? 'neutral' })
  }
  const impacts: ImpactMeta[] = []
  for (const x of Array.isArray(m['impacts']) ? m['impacts'] : []) {
    const o = objet(x)
    const code = texte(o?.['code'])
    const label = texte(o?.['label'])
    if (!o || !code || !label) continue
    impacts.push({ code, label, tone: texte(o['tone']) ?? 'neutral', description: texte(o['description']) })
  }
  const categories = codesLibelles(m['categories'])
  if (statuts.length === 0 || priorites.length === 0 || impacts.length === 0 || categories.length === 0) return null
  const f = objet(m['fichiers'])
  const types: TypeFichier[] = []
  for (const x of Array.isArray(f?.['types']) ? f['types'] as unknown[] : []) {
    const o = objet(x)
    const mime = texte(o?.['mime'])
    const extension = texte(o?.['extension'])
    if (mime && extension) types.push({ mime, extension })
  }
  const limites: Record<string, number> = { ...LIMITES_CONTRAT }
  const l = objet(m['limites'])
  if (l) for (const [k, val] of Object.entries(l)) if (typeof val === 'number' && val > 0) limites[k] = val
  const p = objet(racine?.['permissions'])
  return {
    meta: {
      statuts,
      reouverture_requires: textes(m['reouverture_requires']),
      priorites,
      priorite_defaut: texte(m['priorite_defaut']) ?? priorites[0]!.code,
      impacts,
      impact_defaut: texte(m['impact_defaut']) ?? impacts[0]!.code,
      categories,
      sources: codesLibelles(m['sources']),
      types_relation: codesLibelles(m['types_relation']),
      fichiers: {
        types,
        taille_max: entier(f?.['taille_max']) ?? limites['taille_fichier'] ?? 10485760,
        max_par_signalement: entier(f?.['max_par_signalement']) ?? 20,
      },
      limites,
      evenements: codesLibelles(m['evenements']),
      priorites_elevees: textes(m['priorites_elevees']),
    },
    droits: {
      read: p?.['read'] === true, create: p?.['create'] === true,
      update: p?.['update'] === true, manage: p?.['manage'] === true,
    },
  }
}

/** Libellé d'un code de catalogue ; le code lui-même s'il est inconnu (jamais une case vide). */
export function libelle(liste: readonly CodeLibelle[], code: string | null | undefined): string {
  if (!code) return '—'
  return liste.find((x) => x.code === code)?.label ?? code
}

export function limite(meta: MetaSignalements | null, cle: string): number {
  return meta?.limites[cle] ?? LIMITES_CONTRAT[cle] ?? 200
}

/* ═══════════════════════ ton du catalogue → LIVE CONTROL ═══════════════════════ */

/**
 * `tone` est une INTENTION visuelle de Technique ; GESTION choisit ses
 * couleurs. Un seul mapping, vers des classes qui EXISTENT : `lc-pill--ok`,
 * `--warning`, `--error`, `--idle` (components.css) et `sig-pill--info`
 * (signalements.css, sur `--status-info`, faute de variante info au socle).
 * Couleur + pastille + texte, toujours : le libellé est écrit à côté.
 */
export interface ClassesTon { pill: string; dot: string }

const TONS: Record<string, ClassesTon> = {
  success: { pill: 'lc-pill lc-pill--ok', dot: 'lc-dot lc-dot--ok' },
  warning: { pill: 'lc-pill lc-pill--warning', dot: 'lc-dot lc-dot--warning' },
  danger: { pill: 'lc-pill lc-pill--error', dot: 'lc-dot lc-dot--error' },
  info: { pill: 'lc-pill sig-pill--info', dot: 'lc-dot sig-dot--info' },
  muted: { pill: 'lc-pill lc-pill--idle', dot: 'lc-dot' },
  neutral: { pill: 'lc-pill', dot: 'lc-dot' },
}

export function classesTon(ton: string | null | undefined): ClassesTon {
  return (ton ? TONS[ton] : undefined) ?? TONS['neutral']!
}

/** Le seul modificateur de pastille (`lc-dot--ok`…), pour les options d'un `Select` qui pose déjà `lc-dot`. */
export function modificateurPoint(ton: string | null | undefined): string {
  return classesTon(ton).dot.replace(/^lc-dot\s*/, '')
}

export function tonDe(liste: ReadonlyArray<CodeLibelle & { tone: string }>, code: string | null | undefined): string {
  return liste.find((x) => x.code === code)?.tone ?? 'neutral'
}

/* ═══════════════════════ les objets du domaine ═══════════════════════ */

export interface Identite { provider: string; id: string; display_name: string | null }

function identite(v: unknown): Identite | null {
  const o = objet(v)
  const provider = texte(o?.['provider'])
  const id = ident(o?.['id'])
  if (!provider || !id) return null
  return { provider, id, display_name: texte(o?.['display_name']) }
}

/** Le nom à afficher d'une identité : son instantané, sinon « Compte inconnu » — jamais un identifiant brut. */
export function nomDe(i: Identite | null | undefined, sinon = '—'): string {
  if (!i) return sinon
  return i.display_name?.trim() || 'Compte inconnu'
}

export interface Compteurs { links: number; comments: number; files: number }

/** Un élément de liste (résumé) : ni description, ni liens, ni fichiers. */
export interface Resume {
  id: number
  reference: string
  titre: string
  categorie: string
  priorite: string
  impact: string
  statut: string
  etat: string
  emplacement: string | null
  source: string
  reporter: Identite | null
  responsable: Identite | null
  created_at: string
  updated_at: string
  closed_at: string | null
  version: number
  counts: Compteurs
}

export interface Lien {
  id: number
  service: string
  domain: string
  entity_type: string
  entity_id: string
  relation_type: string
  /** `true` : l'objet a quitté le Parc ; on montre l'instantané, sans lien. `null` : cible externe non vérifiée. */
  target_missing: boolean | null
  snapshot: Objet | null
  created_at: string | null
  created_by: Identite | null
}

export interface Fichier {
  id: number
  nom_original: string
  mime: string
  taille: number
  created_at: string | null
  created_by: Identite | null
}

export interface Fiche extends Resume {
  description: string
  source_reference: string | null
  resolution_summary: string | null
  motif_rejet: string | null
  duplicate_of: { id: number; reference: string } | null
  transitions_possibles: string[]
  links: Lien[]
  files: Fichier[]
}

export function lireResume(v: unknown): Resume | null {
  const o = objet(v)
  if (!o) return null
  const id = entier(o['id'])
  const reference = texte(o['reference'])
  const titre = texte(o['titre'])
  const statut = texte(o['statut'])
  const version = entier(o['version'])
  if (id === null || !reference || titre === null || !statut || version === null) return null
  const c = objet(o['counts'])
  return {
    id, reference, titre, statut, version,
    categorie: texte(o['categorie']) ?? '',
    priorite: texte(o['priorite']) ?? '',
    impact: texte(o['impact']) ?? '',
    etat: texte(o['etat']) ?? '',
    emplacement: texte(o['emplacement']),
    source: texte(o['source']) ?? '',
    reporter: identite(o['reporter']),
    responsable: identite(o['responsable']),
    created_at: texte(o['created_at']) ?? '',
    updated_at: texte(o['updated_at']) ?? '',
    closed_at: texte(o['closed_at']),
    counts: { links: entier(c?.['links']) ?? 0, comments: entier(c?.['comments']) ?? 0, files: entier(c?.['files']) ?? 0 },
  }
}

function lireLien(v: unknown): Lien | null {
  const o = objet(v)
  const id = entier(o?.['id'])
  const domain = texte(o?.['domain'])
  const entityType = texte(o?.['entity_type'])
  const entityId = ident(o?.['entity_id'])
  if (!o || id === null || !domain || !entityType || !entityId) return null
  const tm = o['target_missing']
  return {
    id, domain, entity_type: entityType, entity_id: entityId,
    service: texte(o['service']) ?? '',
    relation_type: texte(o['relation_type']) ?? '',
    target_missing: typeof tm === 'boolean' ? tm : null,
    snapshot: objet(o['snapshot']),
    created_at: texte(o['created_at']),
    created_by: identite(o['created_by']),
  }
}

function lireFichier(v: unknown): Fichier | null {
  const o = objet(v)
  const id = entier(o?.['id'])
  const nom = texte(o?.['nom_original'])
  const mime = texte(o?.['mime'])
  if (!o || id === null || !nom || !mime) return null
  return { id, nom_original: nom, mime, taille: entier(o['taille']) ?? 0, created_at: texte(o['created_at']), created_by: identite(o['created_by']) }
}

/**
 * La fiche complète. `api_path` (chemin interne de Technique) n'est JAMAIS
 * repris ; `download_path`, que le BFF réécrit vers sa propre route, ne l'est
 * pas non plus : l'écran construit cette même adresse GESTION avec
 * `urlFichier` (signalements-api.ts).
 */
export function lireFiche(v: unknown): Fiche | null {
  const o = objet(v)
  const r = lireResume(o)
  if (!o || !r) return null
  const d = objet(o['duplicate_of'])
  const did = entier(d?.['id'])
  const dref = texte(d?.['reference'])
  return {
    ...r,
    description: texte(o['description']) ?? '',
    source_reference: texte(o['source_reference']),
    resolution_summary: texte(o['resolution_summary']),
    motif_rejet: texte(o['motif_rejet']),
    duplicate_of: did !== null && dref ? { id: did, reference: dref } : null,
    transitions_possibles: textes(o['transitions_possibles']),
    links: (Array.isArray(o['links']) ? o['links'] : []).map(lireLien).filter((x): x is Lien => x !== null),
    files: (Array.isArray(o['files']) ? o['files'] : []).map(lireFichier).filter((x): x is Fichier => x !== null),
  }
}

/** `{ signalement }` → la fiche (réponses de lecture, création, PATCH, transitions). */
export function ficheDe(v: unknown): Fiche | null {
  return lireFiche(objet(v)?.['signalement'])
}

/* ═══════════════════════ pages et curseurs ═══════════════════════ */

/**
 * Une page de liste. Le curseur est une valeur OPAQUE : on le rend tel quel à
 * la page suivante, on ne l'analyse jamais et on ne le garde pas au-delà de
 * l'écran (ni adresse, ni stockage). Technique le lie au tri ET aux filtres.
 */
export interface Page<T> { items: T[]; next_cursor: string | null; has_more: boolean }

function page<T>(v: unknown, cle: string, lire: (x: unknown) => T | null): Page<T> | null {
  const o = objet(v)
  const brut = o?.[cle]
  if (!o || !Array.isArray(brut)) return null
  const items = brut.map(lire).filter((x): x is T => x !== null)
  const curseur = texte(o['next_cursor'])
  const encore = o['has_more'] === true && curseur !== null && curseur !== ''
  return { items, next_cursor: encore ? curseur : null, has_more: encore }
}

export function lirePageListe(v: unknown): Page<Resume> | null { return page(v, 'items', lireResume) }

/** Ajoute une page à la suite de la liste déjà affichée, sans doublon (une fiche modifiée peut changer de place). */
export function ajouterPage<T extends { id: number }>(deja: readonly T[], suite: readonly T[]): T[] {
  const vus = new Set(deja.map((x) => x.id))
  return [...deja, ...suite.filter((x) => !vus.has(x.id))]
}

/**
 * Après une erreur de page : `INVALID_CURSOR` (curseur altéré, autre tri,
 * autres filtres, Technique redémarré) → on repart de la PREMIÈRE page, avec le
 * message discret « La liste a été actualisée. » Toute autre erreur s'affiche.
 */
export function suiteApresErreur(e: { code: string }): 'recommencer' | 'afficher' {
  return e.code === 'INVALID_CURSOR' ? 'recommencer' : 'afficher'
}

export const MESSAGE_LISTE_ACTUALISEE = 'La liste a été actualisée.'

/* ═══════════════════════ la synthèse (`GET /summary`) ═══════════════════════ */

export interface Synthese {
  ouverts: number
  nouveaux: number
  pris_en_charge: number
  en_attente: number
  priorite_elevee: number
  non_affectes: number
  plus_ancien_ouvert: { id: number; reference: string; titre: string; created_at: string } | null
  genere_a: string | null
}

/** Les compteurs de Technique, tels quels : jamais recalculés à partir d'une liste. */
export function lireSynthese(v: unknown): Synthese | null {
  const s = objet(objet(v)?.['summary'])
  if (!s) return null
  const n = (k: string) => entier(s[k])
  const valeurs = ['ouverts', 'nouveaux', 'pris_en_charge', 'en_attente', 'priorite_elevee', 'non_affectes'].map(n)
  if (valeurs.some((x) => x === null)) return null
  const pa = objet(s['plus_ancien_ouvert'])
  const paId = entier(pa?.['id'])
  const paRef = texte(pa?.['reference'])
  return {
    ouverts: n('ouverts')!, nouveaux: n('nouveaux')!, pris_en_charge: n('pris_en_charge')!,
    en_attente: n('en_attente')!, priorite_elevee: n('priorite_elevee')!, non_affectes: n('non_affectes')!,
    plus_ancien_ouvert: pa && paId !== null && paRef
      ? { id: paId, reference: paRef, titre: texte(pa['titre']) ?? '', created_at: texte(pa['created_at']) ?? '' }
      : null,
    genere_a: texte(s['genere_a']),
  }
}

/** « 12 ouverts », « 1 ouvert », « Aucun signalement ouvert ». */
export function compte(n: number, singulier: string, pluriel: string): string {
  return `${n} ${n > 1 ? pluriel : singulier}`
}

/* ═══════════════════════ filtres ↔ adresse ═══════════════════════ */

export type Etat = 'active' | 'closed' | 'all'
export type Tri = 'priority' | 'recent'
export type FiltreResponsable =
  | { type: 'tous' }
  | { type: 'non_affecte' }
  | { type: 'affecte' }
  | { type: 'personne'; id: string }

export interface ObjetLie { domain: string; entity_type: string; entity_id: string }

export interface Filtres {
  q: string
  state: Etat
  status: string[]
  priority: string[]
  impact: string[]
  category: string[]
  source: string[]
  responsable: FiltreResponsable
  objet: ObjetLie | null
  sort: Tri
}

export const FILTRES_DEFAUT: Filtres = {
  q: '', state: 'active', status: [], priority: [], impact: [], category: [], source: [],
  responsable: { type: 'tous' }, objet: null, sort: 'priority',
}

const SLUG = /^[a-z][a-z0-9_]{0,31}$/
const ID_PERSONNE = /^[A-Za-z0-9._:@-]{1,128}$/
const ID_OBJET = /^[A-Za-z0-9._:-]{1,128}$/

/** Les codes d'un catalogue, ou `null` avant que `/meta` soit lu (on vérifie alors la seule forme). */
interface Catalogues { status: string[]; priority: string[]; impact: string[]; category: string[]; source: string[] }

export function cataloguesDe(meta: MetaSignalements | null): Catalogues | null {
  if (!meta) return null
  return {
    status: meta.statuts.map((s) => s.code), priority: meta.priorites.map((p) => p.code),
    impact: meta.impacts.map((i) => i.code), category: meta.categories.map((c) => c.code),
    source: meta.sources.map((s) => s.code),
  }
}

/** Plusieurs valeurs : `status=nouveau,en_attente` ou paramètre répété. Valeur inconnue → ignorée, jamais propagée. */
function valeurs(params: URLSearchParams, cle: string, permis: string[] | null): string[] {
  const out: string[] = []
  for (const brut of params.getAll(cle)) {
    for (const v of brut.split(',')) {
      const x = v.trim()
      if (!x || !SLUG.test(x) || (permis && !permis.includes(x)) || out.includes(x)) continue
      out.push(x)
    }
  }
  return out
}

/**
 * Adresse → filtres. Une adresse retouchée à la main ne fait jamais tomber
 * l'écran : chaque valeur est vérifiée, l'inconnu est ignoré. Comme chez
 * Technique, un `status` donné sans `state` vaut « tous les états ».
 */
export function filtresDepuisUrl(params: URLSearchParams, meta: MetaSignalements | null, limiteRecherche = 200): Filtres {
  const cat = cataloguesDe(meta)
  const status = valeurs(params, 'status', cat?.status ?? null)
  const s = params.get('state')
  const state: Etat = s === 'closed' || s === 'all' || s === 'active' ? s : status.length > 0 ? 'all' : 'active'
  const rid = params.get('responsible_id')
  const un = params.get('unassigned')
  const responsable: FiltreResponsable = rid && ID_PERSONNE.test(rid) ? { type: 'personne', id: rid }
    : un === 'true' ? { type: 'non_affecte' } : un === 'false' ? { type: 'affecte' } : { type: 'tous' }
  const d = params.get('domain')
  const t = params.get('entity_type')
  const e = params.get('entity_id')
  const objetLie = d && t && e && SLUG.test(d) && SLUG.test(t) && ID_OBJET.test(e) ? { domain: d, entity_type: t, entity_id: e } : null
  return {
    q: (params.get('q') ?? '').trim().slice(0, limiteRecherche),
    state,
    status: statutsCompatibles(status, state, meta),
    priority: valeurs(params, 'priority', cat?.priority ?? null),
    impact: valeurs(params, 'impact', cat?.impact ?? null),
    category: valeurs(params, 'category', cat?.category ?? null),
    source: valeurs(params, 'source', cat?.source ?? null),
    responsable,
    objet: objetLie,
    sort: params.get('sort') === 'recent' ? 'recent' : 'priority',
  }
}

/** Les statuts qui appartiennent à l'état choisi (« ouverts » n'a pas de « Résolu »). */
export function statutsDeLEtat(meta: MetaSignalements, state: Etat): StatutMeta[] {
  return meta.statuts.filter((s) => state === 'all' || (state === 'active' ? s.actif : !s.actif))
}

function statutsCompatibles(status: string[], state: Etat, meta: MetaSignalements | null): string[] {
  if (!meta || state === 'all') return status
  const permis = statutsDeLEtat(meta, state).map((s) => s.code)
  return status.filter((s) => permis.includes(s))
}

/**
 * Filtres → paramètres, mêmes noms que l'API (un lien partagé se relit tel
 * quel). Les valeurs par défaut ne s'écrivent pas : l'adresse nue est la liste
 * par défaut. Le curseur n'y entre JAMAIS.
 */
export function filtresVersUrl(f: Filtres): URLSearchParams {
  const p = new URLSearchParams()
  if (f.q) p.set('q', f.q)
  // L'état implicite dépend du statut (règle de Technique, reprise à la lecture) :
  // on n'écrit `state` que s'il dit autre chose que ce qu'on relirait sans lui.
  const etatImplicite: Etat = f.status.length > 0 ? 'all' : 'active'
  if (f.state !== etatImplicite) p.set('state', f.state)
  for (const cle of ['status', 'priority', 'impact', 'category', 'source'] as const) {
    if (f[cle].length > 0) p.set(cle, f[cle].join(','))
  }
  if (f.responsable.type === 'personne') p.set('responsible_id', f.responsable.id)
  else if (f.responsable.type === 'non_affecte') p.set('unassigned', 'true')
  else if (f.responsable.type === 'affecte') p.set('unassigned', 'false')
  if (f.objet) {
    p.set('domain', f.objet.domain)
    p.set('entity_type', f.objet.entity_type)
    p.set('entity_id', f.objet.entity_id)
  }
  if (f.sort !== 'priority') p.set('sort', f.sort)
  return p
}

/** La requête de liste vers le BFF : les filtres, l'état explicite, la taille de page et — seulement pour « Charger plus » — le curseur. */
export function requeteListe(f: Filtres, limit: number, curseur: string | null = null): URLSearchParams {
  const p = filtresVersUrl(f)
  p.set('state', f.state)
  p.set('sort', f.sort)
  p.set('limit', String(limit))
  if (curseur) p.set('cursor', curseur)
  return p
}

/** Un filtre est-il posé (au-delà de l'état et du tri) ? */
export function filtresActifs(f: Filtres): boolean {
  return f.q !== '' || f.status.length > 0 || f.priority.length > 0 || f.impact.length > 0
    || f.category.length > 0 || f.source.length > 0 || f.responsable.type !== 'tous' || f.objet !== null
}

/** Deux jeux de filtres donnent-ils la même liste ? (Un curseur ne survit pas à un changement.) */
export function memesFiltres(a: Filtres, b: Filtres): boolean {
  return filtresVersUrl(a).toString() === filtresVersUrl(b).toString()
}

/* ═══════════════════════ actions de statut ═══════════════════════ */

/** Les exigences qu'un formulaire de cet écran sait remplir. */
export const EXIGENCES_CONNUES = ['motif', 'resolution_summary', 'duplicate_of_id'] as const

export interface ActionStatut {
  cible: string
  libelle: string
  /** Depuis un statut fermé vers un statut actif. */
  reouverture: boolean
  exige: string[]
  /** Motif proposé sans être exigé (mise en attente). */
  motifFacultatif: boolean
  /**
   * Un responsable peut être désigné : toute entrée en prise en charge,
   * RÉOUVERTURE COMPRISE. Technique applique la même règle aux deux (sans
   * responsable désigné ni existant, l'acteur le devient) : l'écran doit
   * l'annoncer et laisser choisir quelqu'un d'autre.
   */
  responsableFacultatif: boolean
  sensible: boolean
}

const VERBES: Record<string, string> = {
  pris_en_charge: 'Prendre en charge',
  en_attente: 'Mettre en attente',
  resolu: 'Résoudre',
  rejete: 'Rejeter',
  doublon: 'Marquer comme doublon',
  nouveau: 'Remettre à « Nouveau »',
}

/**
 * Boutons de statut = `transitions_possibles` (Technique, état courant) ∩
 * droit GESTION `manage` ∩ exigences que l'écran sait remplir.
 *
 * L'ordre et la liste viennent de Technique ; cet écran ne décide d'AUCUNE
 * transition. Les exigences : `meta.statuts[cible].requires` pour entrer dans
 * le statut, plus `meta.reouverture_requires` pour sortir d'un statut fermé.
 * Une exigence inconnue (un champ ajouté demain chez Technique) masque
 * l'action : mieux vaut un bouton absent qu'un refus garanti.
 */
export function actionsPermises(
  sig: { statut: string; transitions_possibles: readonly string[] },
  meta: MetaSignalements,
  droits: Droits,
): ActionStatut[] {
  if (!droits.manage) return []
  const courant = meta.statuts.find((s) => s.code === sig.statut)
  const out: ActionStatut[] = []
  for (const cible of sig.transitions_possibles) {
    const m = meta.statuts.find((s) => s.code === cible)
    if (!m || cible === sig.statut || out.some((a) => a.cible === cible)) continue
    const reouverture = !!courant && !courant.actif && m.actif
    const exige = [...new Set([...m.requires, ...(reouverture ? meta.reouverture_requires : [])])]
    if (exige.some((e) => !(EXIGENCES_CONNUES as readonly string[]).includes(e))) continue
    out.push({
      cible,
      libelle: reouverture
        ? (cible === 'pris_en_charge' ? 'Rouvrir et prendre en charge' : 'Rouvrir')
        : VERBES[cible] ?? `Passer à « ${m.label} »`,
      reouverture,
      exige,
      motifFacultatif: !exige.includes('motif') && cible === 'en_attente',
      responsableFacultatif: cible === 'pris_en_charge',
      sensible: !m.actif,
    })
  }
  return out
}

/**
 * La garde d'un changement de statut qui FERME la fiche pendant une saisie
 * (modale « Modifications non enregistrées » du socle, `guardedClose`). On ne
 * quitte pas la page : le texte dit ce qui arrive vraiment, et ses deux issues
 * sont les boutons de la modale (enregistrer d'abord, ou abandonner).
 */
export function texteGardeStatut(libelleAction: string): string {
  return `Vous avez des changements qui n’ont pas été enregistrés. « ${libelleAction} » ferme ce signalement : ses informations ne pourront plus être modifiées. Enregistrez-les d’abord, ou abandonnez-les.`
}

/**
 * L'action d'une modale de statut, RETROUVÉE parmi celles de la fiche relue
 * (après un 409 puis « Actualiser »), ou `null` : plus possible.
 *
 * Retrouvée par sa NATURE, pas par sa seule cible : « Prendre en charge »
 * (depuis « Nouveau ») et « Rouvrir et prendre en charge » (depuis un statut
 * fermé) visent toutes deux `pris_en_charge`. Si la fiche relue n'offre plus
 * que l'autre, la modale ne se transforme pas sous les yeux de l'utilisateur
 * (motif exigé apparu, ou motif saisi abandonné) : elle dit « plus possible »,
 * il la ferme et choisit la nouvelle action en connaissance de cause.
 */
export function retrouverAction(actions: readonly ActionStatut[], ouverte: ActionStatut): ActionStatut | null {
  const memesExigences = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((e) => b.includes(e))
  return actions.find((a) => a.cible === ouverte.cible && a.reouverture === ouverte.reouverture && a.libelle === ouverte.libelle
    && a.motifFacultatif === ouverte.motifFacultatif && a.responsableFacultatif === ouverte.responsableFacultatif
    && memesExigences(a.exige, ouverte.exige)) ?? null
}

/* ═══════════════════════ édition et conflit de version ═══════════════════════ */

/** Les champs modifiables d'une fiche, tels que l'écran les édite. */
export interface Brouillon {
  titre: string
  description: string
  categorie: string
  priorite: string
  impact: string
  emplacement: string
  responsable_id: string | null
}

export function brouillonDe(f: Fiche): Brouillon {
  return {
    titre: f.titre, description: f.description, categorie: f.categorie, priorite: f.priorite,
    impact: f.impact, emplacement: f.emplacement ?? '', responsable_id: f.responsable?.id ?? null,
  }
}

const CHAMPS_INFOS = ['titre', 'description', 'categorie', 'priorite', 'impact', 'emplacement'] as const
type ChampInfo = (typeof CHAMPS_INFOS)[number]

function normaliser(b: Brouillon, champ: ChampInfo): string {
  return champ === 'titre' || champ === 'emplacement' ? b[champ].trim() : b[champ]
}

/**
 * Deux valeurs d'un champ sont-elles LA MÊME pour Technique ? Titre et
 * emplacement se comparent nettoyés, comme le PATCH les envoie
 * (`corpsModification`) : un espace ajouté n'est pas une modification. C'est
 * la SEULE notion de « champ touché » de l'écran — barre, relecture et
 * conflit la partagent, sinon un espace suffisait à garder une valeur
 * périmée par-dessus celle d'un collègue.
 */
function memeValeur(a: Brouillon, b: Brouillon, champ: keyof Brouillon): boolean {
  return champ === 'titre' || champ === 'emplacement' ? normaliser(a, champ) === normaliser(b, champ) : a[champ] === b[champ]
}

/**
 * Le corps d'un PATCH : la `version` LUE et les seuls champs réellement
 * changés. `responsable` n'y entre que si l'écran a le droit de l'éditer.
 * `null` = rien à envoyer (la barre d'enregistrement ne doit pas paraître).
 */
export function corpsModification(
  base: Brouillon, b: Brouillon, version: number, avecResponsable: boolean,
): Record<string, unknown> | null {
  const corps: Record<string, unknown> = {}
  for (const c of CHAMPS_INFOS) {
    if (normaliser(base, c) === normaliser(b, c)) continue
    corps[c] = c === 'emplacement' && b.emplacement.trim() === '' ? null : normaliser(b, c)
  }
  if (avecResponsable && base.responsable_id !== b.responsable_id) corps['responsable_id'] = b.responsable_id
  return Object.keys(corps).length === 0 ? null : { version, ...corps }
}

export function nombreDeModifications(base: Brouillon, b: Brouillon, avecResponsable: boolean): number {
  const c = corpsModification(base, b, 0, avecResponsable)
  return c ? Object.keys(c).length - 1 : 0
}

/** Vérifications locales, AVANT l'envoi — Technique revérifie tout. */
export function validerSaisie(b: Pick<Brouillon, 'titre' | 'description' | 'categorie' | 'emplacement'>, meta: MetaSignalements | null): Record<string, string> {
  const e: Record<string, string> = {}
  const titre = b.titre.trim()
  if (!titre) e['titre'] = 'Le titre est obligatoire.'
  else if (/[\r\n]/.test(titre)) e['titre'] = 'Le titre tient sur une ligne.'
  else if (titre.length > limite(meta, 'titre')) e['titre'] = `${limite(meta, 'titre')} caractères au plus.`
  if (!b.categorie) e['categorie'] = 'Choisissez une catégorie.'
  if (b.description.length > limite(meta, 'description')) e['description'] = `${limite(meta, 'description')} caractères au plus.`
  if (b.emplacement.trim().length > limite(meta, 'emplacement')) e['emplacement'] = `${limite(meta, 'emplacement')} caractères au plus.`
  return e
}

export interface Changement { champ: string; libelle: string; avant: string; apres: string; aussiModifieIci: boolean }

const LIBELLES_CHAMPS: Record<string, string> = {
  statut: 'Statut', titre: 'Titre', description: 'Description', categorie: 'Catégorie',
  priorite: 'Priorité', impact: 'Impact', emplacement: 'Emplacement', responsable: 'Responsable',
}

function court(s: string, n = 140): string {
  const t = s.replace(/\s+/g, ' ').trim()
  return t.length > n ? `${t.slice(0, n - 1)}…` : t || '—'
}

/**
 * CE QUI A CHANGÉ entre la fiche sur laquelle l'utilisateur a commencé à
 * travailler et celle que Technique rend maintenant (409 `VERSION_CONFLICT`
 * puis « Actualiser »). Le brouillon local, s'il est donné, signale les champs
 * que l'utilisateur avait AUSSI modifiés : ce sont eux qu'il doit relire avant
 * de réenregistrer. Rien n'est fusionné ni renvoyé automatiquement.
 */
export function differences(avant: Fiche, apres: Fiche, meta: MetaSignalements, brouillon: Brouillon | null = null): Changement[] {
  const out: Changement[] = []
  const base = brouillonDe(avant)
  const touche = (c: keyof Brouillon) => brouillon !== null && !memeValeur(brouillon, base, c)
  const pousser = (champ: string, a: string, b: string, ici: boolean) => {
    if (a !== b) out.push({ champ, libelle: LIBELLES_CHAMPS[champ] ?? champ, avant: court(a), apres: court(b), aussiModifieIci: ici })
  }
  pousser('statut', libelle(meta.statuts, avant.statut), libelle(meta.statuts, apres.statut), false)
  pousser('titre', avant.titre, apres.titre, touche('titre'))
  pousser('description', avant.description, apres.description, touche('description'))
  pousser('categorie', libelle(meta.categories, avant.categorie), libelle(meta.categories, apres.categorie), touche('categorie'))
  pousser('priorite', libelle(meta.priorites, avant.priorite), libelle(meta.priorites, apres.priorite), touche('priorite'))
  pousser('impact', libelle(meta.impacts, avant.impact), libelle(meta.impacts, apres.impact), touche('impact'))
  pousser('emplacement', avant.emplacement ?? '', apres.emplacement ?? '', touche('emplacement'))
  if ((avant.responsable?.id ?? null) !== (apres.responsable?.id ?? null)) {
    out.push({
      champ: 'responsable', libelle: LIBELLES_CHAMPS['responsable']!,
      avant: nomDe(avant.responsable, 'Personne'), apres: nomDe(apres.responsable, 'Personne'),
      aussiModifieIci: touche('responsable_id'),
    })
  }
  return out
}

/**
 * Après « Actualiser » : le nouveau point de départ est la fiche fraîche, la
 * saisie locale est GARDÉE. Seuls les champs que l'utilisateur n'avait pas
 * touchés suivent la nouvelle valeur — sinon la barre annoncerait comme « sa »
 * modification un retour à l'ancienne valeur d'un collègue.
 *
 * « Touché » se juge comme le PATCH (`memeValeur`) : « Foo␠ » sur « Foo »
 * n'est pas une saisie, il suit le « Bar » d'un collègue. Un champ que
 * Technique n'a pas changé garde ce qui est tapé, espaces compris : rien ne
 * saute sous les doigts.
 */
export function reprendreApresConflit(avant: Fiche, apres: Fiche, brouillon: Brouillon): Brouillon {
  const ancienne = brouillonDe(avant)
  const fraiche = brouillonDe(apres)
  const out = { ...brouillon }
  for (const c of Object.keys(out) as (keyof Brouillon)[]) {
    if (memeValeur(brouillon, ancienne, c) && !memeValeur(fraiche, ancienne, c)) (out as Record<string, unknown>)[c] = fraiche[c]
  }
  return out
}

/**
 * Après un enregistrement RÉUSSI : `envoye` est la saisie partie avec le
 * PATCH, `recue` la fiche que Technique rend, `saisie` ce qui est à l'écran
 * maintenant. Le PATCH peut mettre jusqu'à 10 s et les champs restent
 * modifiables : un champ resté TEL QU'ENVOYÉ suit la réponse (nettoyée par
 * Technique) ; un champ retouché pendant l'envoi — une frappe, même un
 * espace — reste, et la barre reparaît pour lui. Remettre la fiche à zéro
 * effaçait cette frappe sans un mot.
 */
export function apresEnregistrement(envoye: Brouillon, recue: Fiche, saisie: Brouillon): Brouillon {
  const fraiche = brouillonDe(recue)
  const out = { ...saisie }
  for (const c of Object.keys(out) as (keyof Brouillon)[]) {
    if (saisie[c] === envoye[c]) (out as Record<string, unknown>)[c] = fraiche[c]
  }
  return out
}

/** Une fiche est OUVERTE quand son statut est actif au catalogue (à défaut, selon son état). */
export function estOuverte(f: Pick<Fiche, 'statut' | 'etat'>, meta: MetaSignalements): boolean {
  const s = meta.statuts.find((x) => x.code === f.statut)
  return s ? s.actif : f.etat !== 'ferme'
}

/**
 * La saisie locale qui n'est PAS sur la fiche `f` : un `Changement` par champ,
 * `avant` = la fiche, `apres` = la saisie. Sert à la montrer à l'utilisateur
 * quand elle ne peut plus être enregistrée telle quelle.
 */
export function saisieEnAttente(
  f: Fiche, b: Brouillon, meta: MetaSignalements, responsables: readonly Responsable[] = [],
): Changement[] {
  const base = brouillonDe(f)
  const out: Changement[] = []
  const pousser = (champ: string, a: string, v: string) => {
    if (a !== v) out.push({ champ, libelle: LIBELLES_CHAMPS[champ] ?? champ, avant: court(a), apres: court(v), aussiModifieIci: true })
  }
  pousser('titre', normaliser(base, 'titre'), normaliser(b, 'titre'))
  pousser('description', base.description, b.description)
  pousser('categorie', libelle(meta.categories, base.categorie), libelle(meta.categories, b.categorie))
  pousser('priorite', libelle(meta.priorites, base.priorite), libelle(meta.priorites, b.priorite))
  pousser('impact', libelle(meta.impacts, base.impact), libelle(meta.impacts, b.impact))
  pousser('emplacement', normaliser(base, 'emplacement'), normaliser(b, 'emplacement'))
  if (base.responsable_id !== b.responsable_id) {
    const nom = (id: string | null) => (id === null ? 'Personne'
      : id === f.responsable?.id ? nomDe(f.responsable) : responsables.find((r) => r.id === id)?.display_name ?? 'Autre compte')
    pousser('responsable', nom(base.responsable_id), nom(b.responsable_id))
  }
  return out
}

/** Ce que devient l'écran quand une fiche est RELUE pendant une saisie (voir `relireSousSaisie`). */
export interface Relecture {
  /** La saisie rebasée : les champs non touchés suivent la fiche relue (`reprendreApresConflit`). */
  brouillon: Brouillon
  /** Ce qui a changé chez Technique entre les deux lectures (`differences`). */
  changements: Changement[]
  /** La saisie qui ne peut PLUS être enregistrée : la fiche relue est fermée. Vide sinon. */
  bloquees: Changement[]
  /**
   * Une saisie était en cours ET la fiche a changé sous elle : l'écran doit
   * le montrer AVANT tout nouvel enregistrement. Sans cela, le PATCH suivant
   * partirait avec la version relue et écraserait, sans 409, ce qu'un
   * collègue venait de modifier sur le même champ.
   */
  alerter: boolean
}

/**
 * UNE FICHE RELUE PENDANT UNE SAISIE — « Actualiser », ou la relecture qui
 * suit un lien, une pièce jointe, un commentaire.
 *
 * Technique ne change pas la `version` pour un lien, un fichier ou un
 * commentaire : une version qui bouge, c'est un collègue (ou une transition).
 * Relire, c'est donc prendre une nouvelle BASE pour le prochain PATCH ; si
 * l'utilisateur avait une saisie et que la fiche a changé, il doit le voir
 * — même conduite qu'après un 409 `VERSION_CONFLICT` puis « Actualiser » :
 * la saisie est gardée, ce qui a changé est listé, les champs qu'il avait
 * aussi modifiés sont signalés, et c'est lui qui réenregistre.
 */
export function relireSousSaisie(
  avant: Fiche, apres: Fiche, brouillon: Brouillon, meta: MetaSignalements, responsables: readonly Responsable[] = [],
): Relecture {
  const avaitSaisie = corpsModification(brouillonDe(avant), brouillon, avant.version, true) !== null
  const changements = differences(avant, apres, meta, brouillon)
  const rebase = reprendreApresConflit(avant, apres, brouillon)
  return {
    brouillon: rebase,
    changements,
    bloquees: estOuverte(apres, meta) ? [] : saisieEnAttente(apres, rebase, meta, responsables),
    alerter: avaitSaisie && changements.length > 0,
  }
}

/* ═══════════════════════ idempotence par intention ═══════════════════════ */

/**
 * Une clé `[A-Za-z0-9._:-]{8,80}` — un UUID quand le navigateur sait en faire.
 * 80 et non 128 : le BFF préfixe la clé par l'utilisateur (`g-<uuid>-<clé>`)
 * avant de l'envoyer à Technique, qui en accepte 128 au plus.
 */
export function nouvelleCle(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`
}

/** Les refus qui condamnent la clé : consommée chez Technique, ou refusée par le BFF avant tout appel. */
const CLES_A_OUBLIER: readonly string[] = ['IDEMPOTENCY_KEY_REUSED', 'IDEMPOTENCY_KEY_REQUIRED', 'IDEMPOTENCY_KEY_INVALID']

/**
 * UNE CLÉ PAR INTENTION, pas par clic (contrat BFF §6, Technique §8).
 *
 * L'intention est nommée par l'écran (« creation », « commentaire »,
 * « fichier:<nom>:<taille>:<date> »…). La clé naît à la PREMIÈRE tentative et
 * resservira à chaque nouvel essai de la même action — double clic, délai
 * dépassé, « Réessayer » — même si l'utilisateur a corrigé sa saisie entre-
 * temps : si la première tentative avait en fait abouti, Technique répond
 * `IDEMPOTENCY_KEY_REUSED` au lieu de créer un doublon. Elle est oubliée au
 * succès, à l'abandon, quand Technique la déclare déjà consommée, ou quand le
 * BFF la refuse comme absente ou mal formée (`IDEMPOTENCY_KEY_REQUIRED` /
 * `_INVALID` : rien n'est parti chez Technique, et la garder rendrait chaque
 * nouvel essai voué au même refus). Rien ne la conserve hors de la mémoire de
 * l'écran.
 */
export class Intentions {
  readonly #cles = new Map<string, string>()
  readonly #generer: () => string

  constructor(generer: () => string = nouvelleCle) { this.#generer = generer }

  cle(intention: string): string {
    const deja = this.#cles.get(intention)
    if (deja) return deja
    const neuve = this.#generer()
    this.#cles.set(intention, neuve)
    return neuve
  }

  connue(intention: string): boolean { return this.#cles.has(intention) }

  oublier(intention: string): void { this.#cles.delete(intention) }

  toutOublier(): void { this.#cles.clear() }

  /** Après une tentative : succès, clé consommée ou clé refusée par le BFF → oubliée ; tout autre échec la garde pour le prochain essai. */
  apres(intention: string, erreur: { code: string } | null): void {
    if (erreur === null || CLES_A_OUBLIER.includes(erreur.code)) this.oublier(intention)
  }
}

/* ═══════════════════════ erreurs ═══════════════════════ */

export type GenreErreur =
  | 'indisponible' | 'non_configure' | 'integration' | 'interdit' | 'introuvable'
  | 'version' | 'validation' | 'curseur' | 'conflit' | 'debit' | 'fichier' | 'reseau' | 'autre'

export interface ErreurSig {
  genre: GenreErreur
  code: string
  status: number
  message: string
  /** L'identifiant de la requête GESTION : affiché discrètement, il retrouve la trace côté serveur. */
  traceId: string | null
  /** Erreurs par champ (`VALIDATION_FAILED`), sous les noms de champ de l'écran. */
  champs: Record<string, string>
  details: unknown
}

export const MESSAGE_INDISPONIBLE = 'Technique momentanément indisponible. Les autres fonctions de GESTION restent disponibles.'

const MESSAGES: Record<string, string> = {
  TECHNIQUE_UNAVAILABLE: MESSAGE_INDISPONIBLE,
  TECHNIQUE_NOT_CONFIGURED: 'Technique n’est pas encore raccordé à GESTION. Les autres fonctions de GESTION restent disponibles.',
  TECHNIQUE_AUTH_FAILED: 'GESTION n’a pas pu se faire reconnaître par Technique. C’est un problème de raccordement entre les deux services, pas de vos droits : prévenez l’administrateur.',
  // 502 du BFF : Technique a répondu, mais hors contrat. Sur une écriture,
  // l'action a donc PU avoir lieu : on ne promet pas l'inverse.
  TECHNIQUE_INVALID_RESPONSE: 'Technique a répondu dans un format que GESTION ne sait pas lire. Si vous veniez d’enregistrer, l’action a peut-être abouti : actualisez avant de recommencer. Si cela persiste, prévenez l’administrateur.',
  // Erreur de GESTION lui-même (gestionnaire d'erreurs du BFF), pas de Technique.
  INTERNAL_ERROR: 'GESTION a rencontré une erreur interne. Réessayez ; si cela persiste, prévenez l’administrateur.',
  // Refus du BFF AVANT tout appel : rien n'est parti chez Technique.
  IDEMPOTENCY_KEY_REQUIRED: 'GESTION n’a pas pu identifier cette demande : rien n’a été envoyé à Technique. Réessayez.',
  IDEMPOTENCY_KEY_INVALID: 'GESTION n’a pas pu identifier cette demande : rien n’a été envoyé à Technique. Réessayez.',
  RESPONSABLE_INCONNU: 'Le responsable choisi n’est plus un compte actif de GESTION : choisissez quelqu’un d’autre.',
  FORBIDDEN: 'Votre compte GESTION n’a pas le droit de faire cette action.',
  NOT_FOUND: 'Introuvable chez Technique : ce signalement, ce fichier ou ce lien n’existe pas, ou plus.',
  VERSION_CONFLICT: 'Ce signalement a été modifié pendant votre édition.',
  VERSION_REQUIRED: 'La version de la fiche manque : actualisez la fiche puis recommencez.',
  INVALID_CURSOR: MESSAGE_LISTE_ACTUALISEE,
  INVALID_REFERENCE: 'Cette référence de signalement n’est pas valide.',
  UNKNOWN_PARAMETER: 'Un filtre de l’adresse n’est pas reconnu.',
  VALIDATION_FAILED: 'Certaines informations sont à corriger.',
  INVALID_TRANSITION: 'Ce changement de statut n’est plus possible : le signalement a changé entre-temps. Actualisez la fiche.',
  SIGNALEMENT_CLOSED: 'Ce signalement est fermé : rouvrez-le avant de modifier ses informations.',
  DUPLICATE_LINK: 'Ce matériel est déjà lié à ce signalement.',
  LIMIT_EXCEEDED: 'La limite de ce signalement est atteinte.',
  IDEMPOTENCY_KEY_REUSED: 'Une tentative précédente a peut-être déjà abouti. Vérifiez avant de recommencer : le prochain envoi partira comme une nouvelle demande.',
  LINK_TARGET_NOT_FOUND: 'Ce matériel n’existe plus dans le Parc.',
  DUPLICATE_TARGET_NOT_FOUND: 'Le signalement désigné comme original est introuvable.',
  DUPLICATE_TARGET_IS_DUPLICATE: 'Le signalement choisi est lui-même un doublon : désignez plutôt son original.',
  DUPLICATE_SOURCE_HAS_DUPLICATES: 'D’autres signalements sont déjà des doublons de celui-ci : il ne peut pas devenir doublon à son tour.',
  PAYLOAD_TOO_LARGE: 'Fichier trop volumineux.',
  // La liste des types acceptés vient de `/meta` (`messageErreurFichier`), jamais d'ici.
  UNSUPPORTED_MEDIA_TYPE: 'Type de fichier refusé par Technique.',
  RATE_LIMITED: 'Trop de demandes en même temps. Patientez quelques secondes avant de réessayer.',
  STEPUP_REQUIRED: 'Une confirmation forte est nécessaire pour cette action.',
}

function genreDe(status: number, code: string): GenreErreur {
  if (code === 'TECHNIQUE_UNAVAILABLE') return 'indisponible'
  if (code === 'TECHNIQUE_NOT_CONFIGURED') return 'non_configure'
  // Les deux 502 du BFF disent un défaut du RACCORDEMENT (credential refusé,
  // réponse hors contrat) : ni une panne passagère, ni un droit de l'utilisateur.
  if (code === 'TECHNIQUE_AUTH_FAILED' || code === 'TECHNIQUE_INVALID_RESPONSE') return 'integration'
  if (code === 'VERSION_CONFLICT') return 'version'
  if (code === 'INVALID_CURSOR') return 'curseur'
  if (code === 'VALIDATION_FAILED') return 'validation'
  // Une référence mal formée ne désigne rien : réessayer n'y changerait rien.
  if (code === 'INVALID_REFERENCE') return 'introuvable'
  if (status === 403 && code === 'FORBIDDEN') return 'interdit'
  if (status === 404) return 'introuvable'
  if (status === 429) return 'debit'
  if (status === 413 || status === 415) return 'fichier'
  if (status === 409 || status === 422 || status === 428) return 'conflit'
  // Une erreur interne de GESTION n'est pas une absence de Technique.
  if (code === 'INTERNAL_ERROR') return 'autre'
  // Un intermédiaire qui répond sans le corps de GESTION (502/503/504 d'un proxy) :
  // pour l'écran, c'est la même chose — Technique n'est pas joignable par ce chemin.
  if (status >= 500) return 'indisponible'
  return 'autre'
}

/**
 * Noms de champs → noms de l'écran. Technique parle de `responsable` et de
 * `links[i]` ; le BFF, qui reçoit `responsable_id` et `materiel_ids`, nomme
 * un élément fautif `materiel_ids[i]` : tous retombent sur le champ affiché.
 */
function champEcran(field: string): string {
  if (field === 'responsable' || field.startsWith('responsable.')) return 'responsable_id'
  if (field.startsWith('links') || field.startsWith('materiel_ids')) return 'materiel_ids'
  return field
}

function premiereMajuscule(s: string): string { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s }

/**
 * `{ error, message, traceId, details? }` → une erreur dite selon sa CAUSE.
 * Un 2xx rend `null`. Le message de GESTION prime pour les codes que l'écran
 * connaît ; pour les autres, celui du serveur, sinon une phrase générique.
 */
export function lireErreur(status: number, corps: unknown): ErreurSig | null {
  if (status >= 200 && status < 300) return null
  const o = objet(corps)
  const code = texte(o?.['error']) ?? texte(o?.['code']) ?? (status >= 500 ? 'TECHNIQUE_UNAVAILABLE' : 'ERREUR')
  const genre = genreDe(status, code)
  const champs: Record<string, string> = {}
  const details = o?.['details'] ?? null
  if (code === 'VALIDATION_FAILED' && Array.isArray(details)) {
    for (const d of details) {
      const x = objet(d)
      const field = texte(x?.['field'])
      const issue = texte(x?.['issue'])
      if (field && issue && !champs[champEcran(field)]) champs[champEcran(field)] = premiereMajuscule(issue)
    }
  }
  let message = MESSAGES[code] ?? (genre === 'indisponible' ? MESSAGE_INDISPONIBLE : texte(o?.['message']) ?? 'La demande a échoué.')
  // Le responsable refusé par le BFF (compte inactif ou inconnu) : l'erreur va sous SON champ.
  if (code === 'RESPONSABLE_INCONNU') champs['responsable_id'] = message
  const d = objet(details)
  if (code === 'DUPLICATE_TARGET_IS_DUPLICATE' && texte(d?.['original_reference'])) {
    message = `Le signalement choisi est lui-même un doublon de ${texte(d?.['original_reference'])} : désignez plutôt celui-ci.`
  }
  if (code === 'DUPLICATE_SOURCE_HAS_DUPLICATES' && Array.isArray(d?.['duplicates'])) {
    const refs = (d['duplicates'] as unknown[]).map((x) => texte(objet(x)?.['reference'])).filter((x): x is string => !!x)
    if (refs.length > 0) message = `${refs.join(', ')} ${refs.length > 1 ? 'sont déjà des doublons' : 'est déjà un doublon'} de ce signalement : il ne peut pas devenir doublon à son tour.`
  }
  if (code === 'LIMIT_EXCEEDED' && entier(d?.['limit']) !== null) message = `La limite de ${entier(d?.['limit'])} est atteinte pour ce signalement.`
  return { genre, code, status, message, traceId: texte(o?.['traceId']), champs, details }
}

/** Le fetch lui-même a échoué (réseau coupé, serveur GESTION arrêté). */
export function erreurReseau(): ErreurSig {
  return {
    genre: 'reseau', code: 'NETWORK', status: 0,
    message: 'GESTION n’a pas pu joindre son serveur. Vérifiez la connexion puis réessayez.',
    traceId: null, champs: {}, details: null,
  }
}

/** Technique n'est pas là (arrêté, injoignable, non raccordé) : l'écran montre l'état dédié, sans boucle de nouvel essai. */
export function estIndisponible(e: ErreurSig | null): boolean {
  return !!e && (e.genre === 'indisponible' || e.genre === 'non_configure')
}

/* ═══════════════════════ activité (timeline) ═══════════════════════ */

export interface Evenement {
  id: number
  type: string
  created_at: string
  actor: Identite | null
  data: Objet
  comment: { id: number; texte: string } | null
}

function lireEvenement(v: unknown): Evenement | null {
  const o = objet(v)
  const id = entier(o?.['id'])
  const type = texte(o?.['type'])
  if (!o || id === null || !type) return null
  const c = objet(o['comment'])
  const cid = entier(c?.['id'])
  const ct = texte(c?.['texte'])
  return {
    id, type, created_at: texte(o['created_at']) ?? '', actor: identite(o['actor']),
    data: objet(o['data']) ?? {},
    comment: cid !== null && ct !== null ? { id: cid, texte: ct } : null,
  }
}

export function lirePageActivite(v: unknown): Page<Evenement> | null { return page(v, 'activity', lireEvenement) }

export interface LigneActivite {
  titre: string
  /** Phrases lisibles — jamais de JSON brut. */
  details: string[]
  /** Texte libre à montrer en bloc (commentaire, résumé de résolution, motif). */
  texteLibre: string | null
  /** Référence d'un autre signalement à proposer en lien (doublon). */
  reference: string | null
}

/** Une cible de lien racontée humainement, depuis son instantané. */
export function decrireCible(x: { domain?: unknown; entity_type?: unknown; entity_id?: unknown; snapshot?: unknown }): string {
  const s = objet(x.snapshot)
  const num = texte(s?.['numero_inventaire'])
  const nom = texte(s?.['nom'])
  const id = ident(x.entity_id) ?? '?'
  if (x.domain === 'parc' && x.entity_type === 'item') {
    return [num ? `N° ${num}` : `Matériel ${id}`, nom].filter(Boolean).join(' · ')
  }
  if (x.domain === 'parc' && x.entity_type === 'incident') {
    return `Panne${num || nom ? ` de ${[num ? `N° ${num}` : null, nom].filter(Boolean).join(' · ')}` : ` ${id}`}`
  }
  return `${texte(x.domain) ?? 'objet'} ${texte(x.entity_type) ?? ''} ${id}`.replace(/\s+/g, ' ').trim()
}

function valeurLisible(champ: string, v: unknown, meta: MetaSignalements): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v !== 'string') return typeof v === 'number' ? String(v) : '—'
  if (champ === 'categorie') return libelle(meta.categories, v)
  if (champ === 'priorite') return libelle(meta.priorites, v)
  if (champ === 'impact') return libelle(meta.impacts, v)
  return court(v, 90)
}

/**
 * Un événement d'historique → une ligne de timeline. Libellé du type depuis
 * `meta.evenements` ; détails écrits en phrases ; un type inconnu garde son
 * libellé (ou « Événement ») et aucun détail plutôt qu'un objet brut.
 */
export function decrireEvenement(ev: Evenement, meta: MetaSignalements): LigneActivite {
  const titre = meta.evenements.find((e) => e.code === ev.type)?.label ?? 'Événement'
  const d = ev.data
  const details: string[] = []
  let texteLibre: string | null = null
  let reference: string | null = null
  switch (ev.type) {
    case 'created': {
      const p = texte(d['priorite'])
      const i = texte(d['impact'])
      const c = texte(d['categorie'])
      if (c) details.push(`Catégorie ${libelle(meta.categories, c)}`)
      if (p) details.push(`Priorité ${libelle(meta.priorites, p)}`)
      if (i) details.push(`Impact ${libelle(meta.impacts, i)}`)
      const r = identite(d['responsable'])
      if (r) details.push(`Responsable : ${nomDe(r)}`)
      break
    }
    case 'updated': {
      const ch = objet(d['changes'])
      for (const [champ, val] of Object.entries(ch ?? {})) {
        const fv = objet(val)
        if (!fv) continue
        const nom = LIBELLES_CHAMPS[champ] ?? champ
        details.push(champ === 'description'
          ? 'Description modifiée'
          : `${nom} : ${valeurLisible(champ, fv['from'], meta)} → ${valeurLisible(champ, fv['to'], meta)}`)
      }
      break
    }
    case 'priority_changed':
      details.push(`${libelle(meta.priorites, texte(d['from']))} → ${libelle(meta.priorites, texte(d['to']))}`)
      break
    case 'impact_changed':
      details.push(`${libelle(meta.impacts, texte(d['from']))} → ${libelle(meta.impacts, texte(d['to']))}`)
      break
    case 'assigned': {
      const de = identite(d['from'])
      details.push(de ? `${nomDe(de)} → ${nomDe(identite(d['to']))}` : nomDe(identite(d['to'])))
      break
    }
    case 'unassigned':
      details.push(`${nomDe(identite(d['from']))} n’est plus responsable`)
      break
    case 'status_changed':
    case 'reopened': {
      details.push(`${libelle(meta.statuts, texte(d['from']))} → ${libelle(meta.statuts, texte(d['to']))}`)
      const rs = texte(d['resolution_summary'])
      const mo = texte(d['motif'])
      texteLibre = rs ?? mo
      reference = texte(d['duplicate_of_reference'])
      if (reference) details.push(`Doublon de ${reference}`)
      break
    }
    case 'comment_added':
      texteLibre = ev.comment?.texte ?? null
      break
    case 'link_added':
    case 'link_removed':
      details.push(decrireCible(d))
      break
    case 'attachment_added':
    case 'attachment_removed': {
      const nom = texte(d['nom_original'])
      const taille = entier(d['taille'])
      if (nom) details.push(taille !== null ? `${nom} · ${tailleLisible(taille)}` : nom)
      break
    }
    default:
      break
  }
  return { titre, details, texteLibre, reference }
}

/* ═══════════════════════ matériel, responsables ═══════════════════════ */

export interface Materiel {
  id: number
  numero_inventaire: string | null
  nom: string
  marque: string | null
  modele: string | null
  categorie: string | null
  salle: string | null
  etat: string | null
  disponible: boolean | null
}

function lireMateriel(v: unknown): Materiel | null {
  const o = objet(v)
  const id = entier(o?.['id'])
  const nom = texte(o?.['nom'])
  if (!o || id === null || nom === null) return null
  const disp = o['disponible']
  return {
    id, nom,
    numero_inventaire: texte(o['numero_inventaire']), marque: texte(o['marque']),
    modele: texte(o['modele']), categorie: texte(o['categorie']), salle: texte(o['salle']),
    etat: texte(o['etat']), disponible: typeof disp === 'boolean' ? disp : null,
  }
}

export function lirePageMateriel(v: unknown): Page<Materiel> | null { return page(v, 'items', lireMateriel) }

export interface Responsable { id: string; display_name: string; can_handle: boolean }

export function lireResponsables(v: unknown): Responsable[] | null {
  const u = objet(v)?.['users']
  if (!Array.isArray(u)) return null
  const out: Responsable[] = []
  for (const x of u) {
    const o = objet(x)
    const id = texte(o?.['id'])
    const nom = texte(o?.['display_name'])
    if (id && nom) out.push({ id, display_name: nom, can_handle: o?.['can_handle'] === true })
  }
  return out
}

/* ═══════════════════════ fichiers ═══════════════════════ */

export function estImage(mime: string): boolean { return mime.startsWith('image/') }

/** Espaces insécables : « ROOM 1 », « 10 Mo » ne se coupent jamais en fin de ligne (téléphone). */
export function insecable(s: string): string { return s.replace(/ /g, '\u00a0') }

/** Une taille lisible ; le nombre et son unité restent sur la même ligne (espace insécable). */
export function tailleLisible(octets: number): string {
  if (octets < 1024) return `${octets}\u00a0o`
  const ko = octets / 1024
  if (ko < 1024) return `${Math.round(ko)}\u00a0Ko`
  return `${(ko / 1024).toLocaleString('fr-FR', { maximumFractionDigits: 1 })}\u00a0Mo`
}

/**
 * Les types acceptés, tels que `/meta` les annonce : « JPG, PNG, WEBP ou PDF ».
 * Jamais recopiés : un type ajouté ou retiré chez Technique s'affiche ici.
 */
export function typesAcceptes(meta: MetaSignalements): string {
  const ext = [...new Set(meta.fichiers.types.map((t) => t.extension.toUpperCase()))]
  if (ext.length === 0) return 'aucun type annoncé par Technique'
  return ext.length === 1 ? ext[0]! : `${ext.slice(0, -1).join(', ')} ou ${ext[ext.length - 1]!}`
}

/**
 * La taille maximale est une borne EXCLUE : Technique (multer 2.2 / busboy 1.6)
 * refuse déjà un fichier qui fait exactement `taille_max`, bien que son
 * contrat dise « au plus ». On refuse donc avant l'envoi, pas après.
 */
export function fichierTropGros(octets: number, meta: MetaSignalements): boolean {
  return octets >= meta.fichiers.taille_max
}

/**
 * Contrôle AVANT envoi (Technique détecte le vrai type depuis les octets et
 * aura le dernier mot) : type de la liste blanche de `/meta`, taille maximale.
 * `null` = le fichier peut partir.
 */
export function refusFichier(f: { name: string; type: string; size: number }, meta: MetaSignalements): string | null {
  const ext = (f.name.split('.').pop() ?? '').toLowerCase()
  const types = meta.fichiers.types
  const connu = types.some((t) => t.mime === f.type) || types.some((t) => t.extension === ext || (t.extension === 'jpg' && ext === 'jpeg'))
  if (!connu) {
    return /^(heic|heif)$/.test(ext) || /hei[cf]/.test(f.type)
      ? 'Les photos HEIC (iPhone) ne sont pas acceptées : enregistrez-la en JPEG, puis déposez-la.'
      : `Type refusé : ${typesAcceptes(meta)} seulement.`
  }
  if (fichierTropGros(f.size, meta)) return `Fichier trop volumineux : il doit faire moins de ${tailleLisible(meta.fichiers.taille_max)}.`
  if (f.size === 0) return 'Ce fichier est vide.'
  return null
}

/** Le message d'un dépôt refusé ; pour un type refusé, la liste des types acceptés (lue dans `/meta`). */
export function messageErreurFichier(e: ErreurSig, meta: MetaSignalements): string {
  return e.code === 'UNSUPPORTED_MEDIA_TYPE' ? `${e.message} Types acceptés : ${typesAcceptes(meta)}.` : e.message
}

/** L'intention d'un dépôt : le même fichier redéposé est la même intention. */
export function intentionFichier(ref: string, f: { name: string; size: number; lastModified: number }): string {
  return `fichier:${ref}:${f.name}:${f.size}:${f.lastModified}`
}

/* ═══════════════════════ dates, à Paris ═══════════════════════ */

/*
 * Toujours à l'heure de Paris, jamais au fuseau du navigateur : un signalement
 * posé à 23 h 30 dans la nuit du club garde son heure, d'où qu'on le lise.
 * Même construction que les formateurs existants (`heureParis`,
 * `ServiceCredentials`, `MiscPages`).
 */
const FMT_DATE_HEURE = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
})
const FMT_JOUR = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Paris' })

/** « 9 oct. 2026 à 14:32 ». */
export function dateHeureParis(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const parts = FMT_DATE_HEURE.formatToParts(d)
  const v = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? ''
  return `${v('day')} ${v('month')} ${v('year')} à ${v('hour')}:${v('minute')}`
}

/** « 9 oct. 2026 ». */
export function jourParis(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : FMT_JOUR.format(d)
}

/**
 * Une DATE CIVILE (« 2026-10-08 », sans heure : la date d'une panne du Parc)
 * → « 8 oct. 2026 ». Lue à midi UTC, elle reste le même jour à Paris ; une
 * date-heure complète passe par `jourParis` ; une valeur illisible est rendue
 * telle quelle plutôt qu'effacée.
 */
export function dateCivileParis(v: string | null | undefined): string {
  if (!v) return '—'
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const d = new Date(`${v}T12:00:00Z`)
    return Number.isNaN(d.getTime()) ? v : FMT_JOUR.format(d)
  }
  const j = jourParis(v)
  return j === '—' ? v : j
}

/* ═══════════════════════ références ═══════════════════════ */

/** Le motif accepté par le BFF : identifiant numérique ou `SIG-AAAA-NNNN`. */
export const MOTIF_REFERENCE = /^(\d{1,10}|SIG-\d{4}-\d{4,6})$/

export function referenceValide(ref: string): boolean { return MOTIF_REFERENCE.test(ref) }

export function cheminFiche(ref: string): string { return `/technique/signalements/${encodeURIComponent(ref)}` }
